import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Check, Circle, ChevronDown } from 'lucide-react';
import api from '../api';
import { Avatar } from './ui';
import { giornoLabel } from './content';

/**
 * Checklist di avvio del cliente (creata dall'onboarding guidato).
 * I passi sono task con tag "onboarding": spuntarli qui li completa anche in Task.
 * Quando tutto è fatto il riquadro si riduce a una riga "Onboarding completato".
 */
export default function OnboardingChecklist({ clienteId }) {
  const navigate = useNavigate();
  const [dati, setDati] = useState(null);
  const [aperto, setAperto] = useState(true);

  const load = useCallback(() => {
    api.get(`/onboarding/clienti/${clienteId}`, { silent: true }).then(r => setDati(r.data)).catch(() => setDati(null));
  }, [clienteId]);
  useEffect(() => { load(); }, [load]);

  if (!dati || !dati.totale) return null;

  const toggle = async (p) => {
    const nuovo = p.stato === 'completata' ? 'da_fare' : 'completata';
    setDati(d => {
      const passi = d.passi.map(x => (x.id === p.id ? { ...x, stato: nuovo } : x));
      const fatti = passi.filter(x => x.stato === 'completata').length;
      return { ...d, passi, fatti, completato: fatti === passi.length };
    });
    try { await api.put(`/task/${p.id}`, { stato: nuovo }); } catch { load(); }
  };

  const pct = Math.round((dati.fatti / dati.totale) * 100);
  const oggi = new Date().toISOString().slice(0, 10);

  if (dati.completato && !aperto) {
    return (
      <button type="button" onClick={() => setAperto(true)} className="eden-card"
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', marginBottom: 16, cursor: 'pointer', font: 'inherit', color: 'var(--text)', textAlign: 'left' }}>
        <Check size={16} color="var(--green)" /> <b style={{ fontSize: 13.5 }}>Onboarding completato</b>
        <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>{dati.totale} passi</span>
        <ChevronDown size={15} style={{ marginLeft: 'auto', color: 'var(--text-dim)' }} />
      </button>
    );
  }

  return (
    <section className="eden-card" style={{ padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Rocket size={17} color="var(--cyan)" />
        <h2 style={{ fontSize: 14.5, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Avvio del cliente</h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{dati.fatti} di {dati.totale}</span>
        {dati.completato && (
          <button type="button" className="eden-btn is-sm" style={{ marginLeft: 'auto' }} onClick={() => setAperto(false)}>Riduci</button>
        )}
      </div>
      <div style={{ height: 6, borderRadius: 6, background: 'var(--bg3)', overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: dati.completato ? 'var(--green)' : 'var(--cyan)', transition: 'width 0.3s var(--ease)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {dati.passi.map(p => {
          const fatto = p.stato === 'completata';
          const scaduto = !fatto && String(p.data_scadenza) < oggi;
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
              <button type="button" onClick={() => toggle(p)} aria-pressed={fatto} aria-label={fatto ? 'Segna da fare' : 'Segna fatto'}
                className="eden-icon-btn" style={{ width: 28, height: 28, color: fatto ? 'var(--green)' : 'var(--text-dim)', flexShrink: 0 }}>
                {fatto ? <Check size={18} strokeWidth={2.6} /> : <Circle size={18} />}
              </button>
              <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: fatto ? 'var(--text-dim)' : 'var(--text)', textDecoration: fatto ? 'line-through' : 'none' }}>{p.titolo}</div>
                {p.descrizione && !fatto && <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 2 }}>{p.descrizione}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: fatto ? 'var(--text-dim)' : scaduto ? 'var(--red)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {giornoLabel(String(p.data_scadenza).slice(0, 10))}
                </span>
                {p.assegnata_nome && <Avatar name={p.assegnata_nome} size={24} />}
              </div>
            </div>
          );
        })}
      </div>
      <button type="button" className="eden-btn is-sm" style={{ marginTop: 10 }} onClick={() => navigate(`/ped?cliente_id=${clienteId}`)}>Apri il Piano Editoriale</button>
    </section>
  );
}
